import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCart } from '../../context/CartContext';
import { useProductTracking } from '../../hooks/useProductTracking';
import api from '../../config/api';
import './ProductDetail.css';
import { FaArrowLeft, FaInfoCircle, FaShoppingCart, FaExclamationTriangle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { Modal, Button } from 'react-bootstrap';
// SEO bileşenlerini import et
import ProductSchema from '../SEO/ProductSchema';
import ProductMeta from '../SEO/ProductMeta';
import OptimizedImage from '../UI/OptimizedImage';
// Yardımcı fonksiyonları import et
import { enrichProductDescription, generateImageAltText, generateImageTitle } from '../../utils/productHelpers';
// Görüntü hata ayıklama araçlarını import et
import { testImageUrl, getBrowserInfo, monitorImageErrors } from '../../utils/imageDebugger';
// Toast servisini import et
import toastService from '../../services/toastService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartPlus, faSpinner, faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import ShoppingCartSidebar from '../cart/ShoppingCartSidebar';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedColor, setSelectedColor] = useState(null);
  const [availableColors, setAvailableColors] = useState([]);
  const [loadingColors, setLoadingColors] = useState(false);
  const [showColorInfo, setShowColorInfo] = useState(false);
  const { addToCart, cart, updateQuantity, removeFromCart } = useCart();
  const { trackProductView, trackProductClick, trackProductAddToCart, trackProductRemoveFromCart } = useProductTracking();
  
  // Cart items'ı sidebar için formatla
  const formatCartItemsForSidebar = () => {
    return cart.map(item => {
      // Görsel URL'sini daha güvenli şekilde al
      let imageUrl = null;
      if (item.product?.images) {
        // Eğer images bir array ise ilk elemanı al
        if (Array.isArray(item.product.images)) {
          imageUrl = item.product.images[0];
        } else {
          // Eğer images bir string ise direkt kullan
          imageUrl = item.product.images;
        }
      }
      
      // Model ve renk bilgisini birleştir
      const modelInfo = item.selectedModel ? `${item.selectedModel.brand} ${item.selectedModel.model_name}` : 'Model seçilmedi';
      const colorInfo = item.selectedColor ? ` - ${item.selectedColor.name}` : '';
      const variant = modelInfo + colorInfo;
      
      return {
        id: item.product_id || item.product?.id,
        name: item.product?.name || 'Ürün',
        variant: variant,
        price: item.product?.price || 0,
        quantity: item.quantity || 1,
        image: imageUrl,
        color: item.selectedColor
      };
    });
  };
  
  // Stok bilgisi için state
  const [stockInfo, setStockInfo] = useState({
    hasStock: false,
    stockAmount: 0,
    isLoading: false
  });
  
  // Stok uyarısı modalı için state
  const [showStockWarning, setShowStockWarning] = useState(false);
  const [stockWarningMessage, setStockWarningMessage] = useState('');
  
  // Stok durumu mesajı için state
  const [stockStatus, setStockStatus] = useState({
    isChecking: false,
    hasStockIssue: false,
    message: ''
  });
  
  // Ürün özellikleri açılır-kapanır için state
  const [showFeatures, setShowFeatures] = useState(false);
  
  // Product type description state
  const [productTypeDescription, setProductTypeDescription] = useState('');
  
  // Büyüteç özelliği için ref'ler
  const [showZoom, setShowZoom] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const productImageRef = useRef(null);
  const zoomWindowRef = useRef(null);
  const zoomReferenceBoxRef = useRef(null);
  const zoomFactor = 2; // Büyütme faktörü - daha uzak görünmesi için değeri azaltıldı

  // Ürün detay görselleri için state
  const [productDetailImages, setProductDetailImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [preloadedImages, setPreloadedImages] = useState({});
  const [isChangingImage, setIsChangingImage] = useState(false);
  
  // Shopping cart sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Ekran boyutunu kontrol et ve mobil cihaz olup olmadığını belirle
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // İlk yükleme kontrolü
    checkIfMobile();
    
    // Ekran boyutu değiştiğinde kontrol et
    window.addEventListener('resize', checkIfMobile);
    
    // Temizleme fonksiyonu
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/products/${id}`);
        
        // Ürün açıklamasını zenginleştir
        const productData = response.data;
        if (!productData.description || productData.description.trim() === '') {
          productData.description = enrichProductDescription(productData);
        }
        
        setProduct(productData);
        
        // Product type description'ını set et
        if (productData.product_types && productData.product_types.description) {
          setProductTypeDescription(productData.product_types.description);
        }
        
        // Ürün görüntüleme tracking
        trackProductView(productData);
        
        setError(null);

        // Ürün detay görsellerini ayarla
        if (productData.images) {
          // Ana görseli her zaman ilk sıraya ekle
          let allImages = [productData.images];
          
          // Detay görsellerini ekle (eğer varsa)
          if (productData.product_details_images) {
            try {
              // JSON formatındaki verileri parse et
              let detailImages = [];
              if (typeof productData.product_details_images === 'string') {
                detailImages = JSON.parse(productData.product_details_images);
              } else {
                detailImages = productData.product_details_images;
              }
              
              // Eğer array formatta değilse ve geçerli bir URL ise, tek elemanlı bir array oluştur
              if (!Array.isArray(detailImages) && typeof detailImages === 'string' && detailImages.includes('http')) {
                detailImages = [detailImages];
              }
              
              // Boş değilse ve array ise görsel listesini ana görselden sonra ekle
              if (Array.isArray(detailImages) && detailImages.length > 0) {
                allImages = [...allImages, ...detailImages];
              }
            } catch (err) {
              console.error('Error parsing product detail images:', err);
              // Hata durumunda sadece ana görsel kalır
            }
          }
          
          // Tüm görselleri set et
          setProductDetailImages(allImages);
          
          // Görselleri ön yüklemeye başla
          preloadImages(allImages);
        } else {
          // Hiç görsel yoksa boş array
          setProductDetailImages([]);
        }

        // Görüntü URL'sini test et
        if (productData.images) {
          console.log("Testing product image:", productData.images);
          testImageUrl(productData.images)
            .then(result => console.log("Image test result:", result))
            .catch(err => console.error("Image test error:", err));
        }

        // Tarayıcı bilgilerini logla
        getBrowserInfo();
        
        // Görüntü hatalarını izle
        monitorImageErrors();
      } catch (err) {
        setError('Ürün yüklenirken bir hata oluştu.');
        console.error('Error fetching product:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // Görselleri önceden yükle
  const preloadImages = (images) => {
    if (!images || images.length === 0) return;
    
    const imageCache = {};
    
    images.forEach((src, index) => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        imageCache[index] = src;
        setPreloadedImages(prev => ({...prev, [index]: src}));
      };
      img.onerror = (err) => {
        console.error(`Failed to preload image at index ${index}:`, err);
      };
    });
  };

  // Telefon modeli değiştiğinde renk seçeneklerini güncelle
  useEffect(() => {
    const fetchColorOptions = async () => {
      if (!selectedModel) {
        setAvailableColors([]);
        setSelectedColor(null);
        setStockInfo({
          hasStock: false,
          stockAmount: 0,
          isLoading: false
        });
        setStockStatus({
          isChecking: false,
          hasStockIssue: false,
          message: ''
        });
        return;
      }

      try {
        // Renk seçeneklerinin yüklendiğini belirt
        setLoadingColors(true);
        setStockInfo(prev => ({ ...prev, isLoading: true }));
        setStockStatus({
          isChecking: true,
          hasStockIssue: false,
          message: 'Stok bilgisi kontrol ediliyor...'
        });
        
        // Seçilen telefon modeli için renk seçeneklerini ve stok bilgisini getir (product_type'a göre)
        const productTypeId = product?.product_type_id;
        const apiUrl = productTypeId 
          ? `/phone-models/${selectedModel}/colors?product_type_id=${productTypeId}`
          : `/phone-models/${selectedModel}/colors`;
        const response = await api.get(apiUrl);
        
        // Renk seçenekleri var mı kontrol et
        if (response.data && response.data.length > 0) {
          // API'den gelen renk verilerini zenginleştir
          const enrichedColors = response.data.map(color => ({
            ...color,
            hex_code: color.code, // code alanını hex_code olarak kullan
            checkedStock: false, // Stok kontrolü yapılıp yapılmadığını belirtmek için
            actualStock: null // Ürüne özgü gerçek stok bilgisi
          }));
          
          // Her renk için gerçek stok bilgisini al
          const colorsWithStock = await Promise.all(
            enrichedColors.map(async (color) => {
              try {
                // Ürün, model ve renk için stok kontrolü
                const stockResponse = await api.get(`/products/${id}/models/${selectedModel}/colors/${color.id}/stock`);
                return {
                  ...color,
                  checkedStock: true,
                  actualStock: stockResponse.data.stock || 0,
                  stock: stockResponse.data.stock || 0 // Orijinal stok değerini güncelle
                };
              } catch (error) {
                console.error(`Error checking stock for color ${color.name}:`, error);
                return {
                  ...color,
                  checkedStock: true,
                  actualStock: 0,
                  stock: 0 // Hata durumunda stok 0 olarak işaretle
                };
              }
            })
          );
          
          // Renkleri ve stok bilgisini ayarla
          setAvailableColors(colorsWithStock);
          
          // Stok bilgilerini kontrol et
          const anyStockAvailable = colorsWithStock.some(color => color.stock > 0);
          
          setStockInfo({
            hasStock: anyStockAvailable,
            stockAmount: 0, // Henüz renk seçilmediği için 0
            isLoading: false
          });
          
          // Sepetteki ürünlerin stok kontrolünü yap
          await checkCartStockForModel(selectedModel, colorsWithStock);
          
          console.log("Renk bilgileri güncellendi:", colorsWithStock);
        } else {
          // API'den renk bilgisi gelmedi veya boş array geldi
          console.log("Renk seçenekleri bulunamadı");
          
          // Boş array set et
          setAvailableColors([]);
          
          setStockInfo({
            hasStock: false,
            stockAmount: 0,
            isLoading: false
          });
          
          setStockStatus({
            isChecking: false,
            hasStockIssue: true,
            message: 'Bu model için renk seçeneği bulunmuyor'
          });
        }
        
        setSelectedColor(null); // Renk seçimini sıfırla
      } catch (err) {
        console.error('Error fetching color options:', err);
        toast.error('Renk seçenekleri yüklenirken bir hata oluştu');
        
        // Hata durumunda boş array set et
        setAvailableColors([]);
        
        setStockInfo({
          hasStock: false,
          stockAmount: 0,
          isLoading: false
        });
        
        setStockStatus({
          isChecking: false,
          hasStockIssue: true,
          message: 'Stok bilgisi alınamadı'
        });
      } finally {
        // Yükleme işlemi tamamlandı
        setLoadingColors(false);
      }
    };

    fetchColorOptions();
  }, [selectedModel, cart, id]);

  // Seçilen model için sepetteki miktarı kontrol et ve stok uyarısı göster
  const checkCartStockForModel = async (modelId, colors) => {
    if (!colors || colors.length === 0) return;
    
    try {
      // Her renk için sepetteki öğeleri kontrol et
      const colorsWithCartInfo = await Promise.all(
        colors.map(async (color) => {
          // Bu ürün, model ve renk için sepetteki öğeleri bul
          const cartItemsWithSameModelAndColor = cart.filter(item => {
            const itemModelId = item.selectedModel?.id || item.phone_model?.id || item.phone_model_id;
            const itemColorId = item.selectedColor?.id || item.color?.id || item.color_id;
            return Number(itemModelId) === Number(modelId) && Number(itemColorId) === Number(color.id);
          });
          
          // Sepetteki toplam miktar
          const cartQuantity = cartItemsWithSameModelAndColor.reduce((sum, item) => sum + item.quantity, 0);
          
          // API'den alınan stok miktarı
          const availableStock = color.stock || 0;
          
          // Kalan stok miktarı
          const remainingStock = Math.max(0, availableStock - cartQuantity);
          
          return {
            ...color,
            cartQuantity,
            remainingStock,
            hasCartStock: cartQuantity > 0,
            isStockFull: cartQuantity >= availableStock
          };
        })
      );
      
      // Modelin toplam stok miktarı
      const totalModelStock = colorsWithCartInfo.reduce((sum, color) => sum + (color.stock || 0), 0);
      
      // Modelin toplam sepet miktarı
      const totalCartQuantity = colorsWithCartInfo.reduce((sum, color) => sum + color.cartQuantity, 0);
      
      // Tamamen tükenmiş renklerin sayısı
      const outOfStockColors = colorsWithCartInfo.filter(color => color.isStockFull).length;
      
      // Stok tükendiyse veya tüm stok sepetteyse uyarı göster
      if (totalCartQuantity >= totalModelStock) {
        setShowStockWarning(true);
        setStockWarningMessage(`Bu telefon modeline ait tüm renklerin stok limiti dolmuştur. (${totalCartQuantity}/${totalModelStock})`);
        setStockStatus({
          isChecking: false,
          hasStockIssue: true,
          message: `Sepetinizde bu modelden yeterli sayıda ürün bulunuyor! (${totalCartQuantity}/${totalModelStock})`
        });
      } else if (outOfStockColors > 0 && outOfStockColors === colorsWithCartInfo.length) {
        // Tüm renkler tükenmişse
        setStockStatus({
          isChecking: false,
          hasStockIssue: true,
          message: 'Bu model için sepetinizde yeterli sayıda ürün bulunuyor!'
        });
      } else if (totalModelStock === 0) {
        // Modelde hiç stok yoksa
        setStockStatus({
          isChecking: false,
          hasStockIssue: true,
          message: 'Bu model için stokta ürün bulunmuyor!'
        });
      } else {
        // Her şey normal, renk seçimine devam et
        setShowStockWarning(false);
        setStockWarningMessage('');
        setStockStatus({
          isChecking: false,
          hasStockIssue: false,
          message: ''
        });
        
        // Güncellenen stok bilgisini içeren renkleri tekrar set et
        setAvailableColors(colors.map(color => {
          const colorInfo = colorsWithCartInfo.find(c => c.id === color.id);
          if (colorInfo) {
            return {
              ...color,
              stock: colorInfo.remainingStock, // Kalan stok bilgisi
              cartQuantity: colorInfo.cartQuantity,
              originalStock: color.stock // Orijinal stok bilgisini sakla
            };
          }
          return color;
        }));
        
        // Stok bilgisini güncelle (renk seçildiyse)
        if (selectedColor) {
          const selectedColorInfo = colorsWithCartInfo.find(c => c.id === selectedColor.id);
          if (selectedColorInfo) {
            setStockInfo({
              hasStock: selectedColorInfo.remainingStock > 0,
              stockAmount: selectedColorInfo.remainingStock,
              isLoading: false
            });
          }
        }
      }
    } catch (error) {
      console.error('Cart stock check error:', error);
      setStockStatus({
        isChecking: false,
        hasStockIssue: true,
        message: 'Stok kontrolü sırasında bir hata oluştu'
      });
    }
  };

  const handleModelSelect = (e) => {
    setSelectedModel(parseInt(e.target.value));
  };

  const handleColorSelect = (color) => {
    // Kalan stok veya orijinal stok değerine bakılarak kontrol yapılır
    const availableStock = color.remainingStock !== undefined ? color.remainingStock : color.stock;
    
    // Stokta olmayan renkleri seçmeyi engelle
    if (availableStock <= 0) {
      const inCartMessage = color.cartQuantity > 0 
        ? `${color.name} rengi sepetinizde bulunmaktadır! (${color.cartQuantity}/${color.originalStock || color.stock})`
        : `${color.name} rengi için stok bulunmamaktadır!`;
        
      toast.error(inCartMessage);
      return;
    }
    
    setSelectedColor(color);
    
    // Seçilen renge göre stok bilgisini güncelle
    if (color) {
      // Rengin kalan stok bilgisini kullan
      const stockAmount = color.remainingStock !== undefined ? color.remainingStock : color.stock;
      
      setStockInfo({
        hasStock: stockAmount > 0,
        stockAmount: stockAmount,
        isLoading: false
      });
      
      console.log(`Renk [${color.name}] seçildi, kalan stok: ${stockAmount}, sepetteki: ${color.cartQuantity || 0}`);
    } else {
      setStockInfo({
        hasStock: false,
        stockAmount: 0,
        isLoading: false
      });
    }
  };

  const handleColorInfoClick = (e) => {
    e.preventDefault();
    setShowColorInfo(true);
  };

  const handleCloseColorInfo = () => {
    setShowColorInfo(false);
  };

  // Stok uyarısı modalını kapat
  const handleCloseStockWarning = () => {
    setShowStockWarning(false);
  };

  // Görsel değiştirme fonksiyonu - geliştirilen sürüm
  const handleImageSelect = (index) => {
    if (index === selectedImageIndex) return;
    setSelectedImageIndex(index);
  };

  // Büyüteç fonksiyonları
  const handleMouseEnter = () => {
    if (!isMobile) {
      setShowZoom(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setShowZoom(false);
    }
  };

  // Optimize edilmiş mouse move handler
  const handleMouseMove = (e) => {
    if (isMobile) return;
    
    if (!productImageRef.current || !zoomWindowRef.current || !zoomReferenceBoxRef.current) return;
    
    const { left, top, width, height } = productImageRef.current.getBoundingClientRect();
    
    const mouseX = e.clientX - left;
    const mouseY = e.clientY - top;
    
    const referenceBoxWidth = width / (zoomFactor + 0.5);
    const referenceBoxHeight = height / (zoomFactor + 0.5);
    
    let boxLeft = mouseX - (referenceBoxWidth / 2);
    let boxTop = mouseY - (referenceBoxHeight / 2);
    
    boxLeft = Math.max(0, Math.min(width - referenceBoxWidth, boxLeft));
    boxTop = Math.max(0, Math.min(height - referenceBoxHeight, boxTop));
    
    const zoomX = boxLeft / (width - referenceBoxWidth || 1);
    const zoomY = boxTop / (height - referenceBoxHeight || 1);
    
    zoomReferenceBoxRef.current.style.left = `${boxLeft}px`;
    zoomReferenceBoxRef.current.style.top = `${boxTop}px`;
    zoomReferenceBoxRef.current.style.width = `${referenceBoxWidth}px`;
    zoomReferenceBoxRef.current.style.height = `${referenceBoxHeight}px`;
    
    zoomWindowRef.current.style.backgroundPosition = `${zoomX * 100}% ${zoomY * 100}%`;
  };

  const handleAddToCart = () => {
    // Model seçilmemişse işlemi durdur ve uyarı göster
    if (!selectedModel) {
      toast.warn('Lütfen telefon modeli seçiniz!');
      return;
    }

    // Renkler yükleniyorsa işlemi durdur
    if (loadingColors) {
      return;
    }
    
    // Kullanılabilir renk yoksa işlemi durdur
    if (availableColors.length === 0) {
      toast.error('Bu model için stok bilgisi bulunamadı!');
      return;
    }

    // Renk seçilmemişse işlemi durdur ve uyarı göster
    if (!selectedColor) {
      toast.warn('Lütfen renk seçiniz!');
      return;
    }

    // Stok kontrolü - kalan stok değerini kullan
    const remainingStock = selectedColor.remainingStock !== undefined ? 
      selectedColor.remainingStock : selectedColor.stock;
    
    if (remainingStock <= 0) {
      const inCartMessage = selectedColor.cartQuantity > 0 
        ? `Bu renk sepetinizde bulunmaktadır! (${selectedColor.cartQuantity}/${selectedColor.originalStock || selectedColor.stock})`
        : 'Bu ürün stokta bulunmamaktadır!';
      
      toast.error(inCartMessage);
      return;
    }
    
    // Stok sorunu varsa işlemi durdur
    if (stockStatus.hasStockIssue) {
      toast.error(stockStatus.message || 'Stok problemi nedeniyle ürün sepete eklenemiyor');
      return;
    }

    try {
      // Seçilen modelin tam bilgilerini bul
      const selectedModelDetails = product.phone_models?.find(
        model => model.id === parseInt(selectedModel)
      );

      if (!selectedModelDetails) {
        toastService.showModelNotFoundToast();
        return;
      }

      console.log('ProductDetail selectedModelDetails:', selectedModelDetails);
      addToCart(product, selectedModelDetails, selectedColor, 1);
      
      // Ürün sepete ekleme tracking
      trackProductAddToCart(product);
      
      // Sidebar'ı aç
      setIsSidebarOpen(true);
    } catch (error) {
      console.error('Sepete ekleme hatası:', error);
      toastService.showCartErrorToast('Ürün sepete eklenirken bir hata oluştu');
    }
  };

  const handleBackToProducts = () => {
    navigate('/products');
  };

  // Sidebar handler fonksiyonları
  const handleUpdateQuantity = async (productId, variant, newQuantity) => {
    try {
      console.log('🔄 handleUpdateQuantity called:', { productId, variant, newQuantity });
      
      // Variant'tan model ve renk bilgisini çıkar
      const variantParts = variant.split(' - ');
      const modelInfo = variantParts[0];
      const colorInfo = variantParts[1];
      
      // Cart item'ı model ve renk bilgisine göre bul
      const cartItem = cart.find(item => {
        const itemModelInfo = item.selectedModel ? `${item.selectedModel.brand} ${item.selectedModel.model_name}` : '';
        const itemColorInfo = item.selectedColor ? item.selectedColor.name : '';
        const itemVariant = itemModelInfo + (itemColorInfo ? ` - ${itemColorInfo}` : '');
        
        return itemVariant === variant;
      });
      
      if (!cartItem) {
        console.error('Cart item bulunamadı:', { productId, variant });
        return;
      }
      
      // Model ve color ID'lerini al
      const modelId = cartItem.selectedModel?.id || cartItem.phone_model_id;
      const colorId = cartItem.selectedColor?.id || cartItem.color_id;
      
      console.log('🔄 Calling updateQuantity with:', { productId, modelId, colorId, newQuantity });
      await updateQuantity(productId, modelId, colorId, newQuantity);
    } catch (error) {
      console.error('Miktar güncelleme hatası:', error);
      
      // Stok yetersizliği kontrolü
      if (error.response?.data?.error === 'Yetersiz stok' || 
          error.response?.data?.error === 'Bu model için toplam sepet limiti aşıldı') {
        toast.error(error.response.data.error);
      } else {
        toast.error('Miktar güncellenirken bir hata oluştu');
      }
    }
  };

  const handleRemoveItem = async (productId, variant) => {
    try {
      console.log('🗑️ handleRemoveItem called:', { productId, variant });
      
      // Variant'tan model ve renk bilgisini çıkar
      const variantParts = variant.split(' - ');
      const modelInfo = variantParts[0];
      const colorInfo = variantParts[1];
      
      // Cart item'ı model ve renk bilgisine göre bul
      const cartItem = cart.find(item => {
        const itemModelInfo = item.selectedModel ? `${item.selectedModel.brand} ${item.selectedModel.model_name}` : '';
        const itemColorInfo = item.selectedColor ? item.selectedColor.name : '';
        const itemVariant = itemModelInfo + (itemColorInfo ? ` - ${itemColorInfo}` : '');
        
        return itemVariant === variant;
      });
      
      if (!cartItem) {
        console.error('Cart item bulunamadı:', { productId, variant });
        return;
      }
      
      // Model ve color ID'lerini al
      const modelId = cartItem.selectedModel?.id || cartItem.phone_model_id;
      const colorId = cartItem.selectedColor?.id || cartItem.color_id;
      
      console.log('🗑️ Calling removeFromCart with:', { productId, modelId, colorId });
      
      // Tracking için product bilgisini al
      const productForTracking = cartItem.product || { id: productId, name: cartItem.product_name || 'Ürün' };
      trackProductRemoveFromCart(productForTracking);
      
      await removeFromCart(productId, modelId, colorId);
      toast.success('Ürün sepetten kaldırıldı');
    } catch (error) {
      console.error('Ürün kaldırma hatası:', error);
      toast.error('Ürün kaldırılırken bir hata oluştu');
    }
  };

  const handleViewCart = () => {
    setIsSidebarOpen(false);
    navigate('/cart');
  };

  const handleCheckout = () => {
    setIsSidebarOpen(false);
    navigate('/checkout');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="spinner text-primary">
            <FontAwesomeIcon icon={faSpinner} className="fa-spin fa-3x" />
          </div>
          <div className="loading-text">Ürün Yükleniyor</div>
          <div className="loading-progress">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-detail-error">
        <p>{error}</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-error">
        <p>Ürün bulunamadı.</p>
      </div>
    );
  }

  return (
    <div className="product-detail-page overflow-hidden">
      {/* Paralaks Arka Plan */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-white opacity-90 z-0"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-amber-50/80 to-white/90 z-1"></div>
        <div className="absolute inset-0 z-0" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1586075010923-2dd4570fb338?q=80&w=2787&auto=format&fit=crop')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(3px) brightness(0.7) contrast(0.9)',
          transform: 'scale(1.1)',
          mixBlendMode: 'multiply'
        }}></div>
      </div>
      
      {/* Dekoratif Şekiller */}
      <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-amber-300 opacity-20"></div>
      <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full bg-amber-500 opacity-10"></div>
      <div className="absolute top-1/3 right-1/4 w-24 h-24 rounded-full bg-amber-700 opacity-10"></div>

      <div className="product-detail-container page-transition relative z-10">
        {/* SEO Bileşenleri */}
        {product && (
          <>
            <ProductSchema product={product} />
            <ProductMeta product={product} />
          </>
        )}

        <div className="back-to-products">
          <button onClick={handleBackToProducts} className="back-button">
            <FaArrowLeft /> <span>Ürünlere Dön</span>
          </button>
          <button 
            onClick={() => setIsSidebarOpen(true)}
            className="cart-button"
            style={{
              position: 'absolute',
              right: '20px',
              top: '20px',
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              zIndex: 10
            }}
          >
            <FontAwesomeIcon icon={faShoppingCart} className="text-gray-700" />
            {cart.length > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: '-5px',
                  right: '-5px',
                  background: '#ef4444',
                  color: 'white',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}
              >
                {cart.length}
              </span>
            )}
          </button>
        </div>
        <div className="product-detail-content">
          <div className="product-image-gallery">
            {/* Dikey küçük resim şeridi */}
            <div className="product-thumbnails">
              {productDetailImages.map((image, index) => (
                <div 
                  key={index} 
                  className={`product-thumbnail ${selectedImageIndex === index ? 'active' : ''}`}
                  onClick={() => handleImageSelect(index)}
                >
                  <OptimizedImage 
                    src={image} 
                    alt={`${product.name} - Görsel ${index + 1}`}
                    width="100%"
                    height="auto"
                  />
                </div>
              ))}
            </div>
            
            {/* Ana ürün görseli */}
            <div 
              className="product-detail-image"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onMouseMove={handleMouseMove}
              ref={productImageRef}
            >
              {productDetailImages.length > 0 ? (
                <div className="product-image-wrapper">
                  <img 
                    src={productDetailImages[selectedImageIndex]} 
                    alt={generateImageAltText(product) || `${product.name} - Görsel ${selectedImageIndex + 1}`}
                    title={generateImageTitle(product) || `${product.name} - Mr. Brown Case özel tasarım telefon kılıfı`}
                    className="product-image"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      maxWidth: '100%',
                      maxHeight: '100%'
                    }}
                    onError={(e) => {
                      console.error("Görüntü yüklenirken hata:", e);
                      // Hata durumunda alternatif görüntüyü dene
                      if (product.no_mask) {
                        e.target.src = product.no_mask;
                      }
                    }}
                  />
                </div>
              ) : (
                <img 
                  src={product.images} 
                  alt={generateImageAltText(product) || `${product.name} - Telefon Kılıfı`}
                  title={generateImageTitle(product) || `${product.name} - Mr. Brown Case özel tasarım telefon kılıfı`}
                  className="product-image"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    maxWidth: '100%',
                    maxHeight: '100%'
                  }}
                  onError={(e) => {
                    console.error("Görüntü yüklenirken hata:", e);
                    // Hata durumunda alternatif görüntüyü dene
                    if (product.no_mask) {
                      e.target.src = product.no_mask;
                    }
                  }}
                />
              )}
              
              {/* Referans karesi - büyütme faktörüne göre boyutlandırılmış */}
              {showZoom && !isMobile && (
                <div 
                  className="zoom-reference-box"
                  ref={zoomReferenceBoxRef}
                />
              )}
            </div>
          </div>
          <div className="product-detail-info">
            {/* Büyüteç penceresi - ürün açıklamasının üzerine gelecek şekilde */}
            {showZoom && !isMobile && productDetailImages.length > 0 && (
              <div 
                className="image-zoom-window square-zoom"
                ref={zoomWindowRef}
                style={{
                  backgroundImage: `url(${productDetailImages[selectedImageIndex]})`,
                  backgroundSize: `${zoomFactor * 100}% ${zoomFactor * 100}%`
                }}
              />
            )}
            <h1 className="product-detail-title">{product.name}</h1>
            <div className="product-detail-collection">
              <span className="collection-label">Koleksiyon:</span>
              <span className="collection-name">{product.collections.name}</span>
            </div>
            <p className="product-detail-description">{product.description}</p>
            
            {/* Ürün Özellikleri Bölümü */}
            <div className="product-features">
              <div 
                className="features-title-container" 
                onClick={() => setShowFeatures(!showFeatures)}
              >
                <h3 className="features-title">✨ Ürün Özellikleri</h3>
                <span className="features-toggle-icon">
                  {showFeatures ? <FaChevronUp /> : <FaChevronDown />}
                </span>
              </div>
              
              <div className={`features-content ${showFeatures ? 'expanded' : 'collapsed'}`}>
                <div className="features-list">
                  {/* Product Type Description - Sadece dynamic content */}
                  {productTypeDescription && (
                    <div className="feature-item product-type-feature">
                      <span className="feature-icon">🏷️</span>
                      <div className="feature-content">
                        <strong>
                          {product.product_types ? product.product_types.display_name : 'Ürün Tipi'}
                        </strong>
                        <p>{productTypeDescription}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="product-detail-price">
              <span className="price-label">Fiyat:</span>
              <span className="price-amount">{product.price} TL</span>
            </div>
            <div className="product-stock">
              <span className="stock-label">Stok Durumu: </span>
              {!selectedModel ? (
                <span className="stock-amount warning">Lütfen model seçiniz</span>
              ) : loadingColors || stockStatus.isChecking ? (
                <span className="stock-amount loading">Yükleniyor</span>
              ) : availableColors.length === 0 ? (
                <span className="stock-amount unavailable">Bu model için renk seçeneği bulunmamaktadır</span>
              ) : !selectedColor ? (
                <span className="stock-amount info">Lütfen renk seçiniz</span>
              ) : stockInfo.hasStock ? (
                <span className="stock-amount available">{stockInfo.stockAmount} Adet</span>
              ) : (
                <span className="stock-amount unavailable">Tükendi</span>
              )}
            </div>
            <div className="product-detail-models">
              <h3>Uyumlu Modeller:</h3>
              <div className="model-dropdown-container">
                <select 
                  className="model-dropdown" 
                  value={selectedModel || ''} 
                  onChange={handleModelSelect}
                >
                  <option value="">Telefon modeli seçiniz</option>
                  {product.phone_models.map((model) => (
                    <option 
                      key={model.id} 
                      value={model.id}
                    >
                      {model.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Stok durumu uyarı mesajı */}
            {selectedModel && stockStatus.hasStockIssue && (
              <div className="stock-status-message warning">
                <FaExclamationTriangle className="warning-icon" />
                <span>{stockStatus.message}</span>
              </div>
            )}
            
            {selectedModel && (loadingColors || stockStatus.isChecking ? (
              <div className="product-detail-colors loading">
                <div className="colors-heading">
                  <h3>Renk Seçenekleri Yükleniyor...</h3>
                </div>
                <div className="color-loading">
                  <div className="color-loading-indicator"></div>
                </div>
              </div>
            ) : availableColors.length > 0 && (
              <div className="product-detail-colors">
                <div className="colors-heading">
                  <h3>Çerçeve Rengi:</h3>
                  <FaInfoCircle 
                    className="color-info-icon" 
                    onClick={handleColorInfoClick}
                  />
                </div>
                <div className="color-list">
                  {availableColors.map((color) => {
                    const isSelected = selectedColor?.id === color.id;
                    const isOutOfStock = color.stock <= 0;
                    const colorCode = color.hex_code;
                    const isWhiteColor = colorCode === '#FFFFFF' || colorCode === 'white' || colorCode === '#FFF';
                    const isTransparentColor = colorCode === 'transparent' || colorCode === 'rgba(0,0,0,0)';
                    const isDarkColor = colorCode === '#000000' || colorCode === 'black' || colorCode === '#000';
                    const isLightColor = isWhiteColor || isTransparentColor;
                    
                    return (
                      <div
                        key={color.id}
                        className={`color-item 
                          ${isSelected ? 'selected' : ''} 
                          ${isTransparentColor ? 'transparent-color' : ''} 
                          ${isWhiteColor ? 'white-color' : ''} 
                          ${isOutOfStock ? 'out-of-stock' : ''}`}
                        style={{
                          backgroundColor: colorCode,
                          border: isLightColor ? '2px solid #ddd' : 'none',
                          opacity: isOutOfStock ? 0.6 : 1
                        }}
                        onClick={() => !isOutOfStock && handleColorSelect(color)}
                        title={`${color.name}${isOutOfStock ? ' (Tükendi)' : ''}`}
                      >
                        {isTransparentColor && <div className="transparent-pattern"></div>}
                        {isSelected && (
                          <span className={`color-check ${isLightColor ? 'dark-check' : ''} ${isDarkColor ? 'light-check' : ''}`}>
                            ✓
                          </span>
                        )}
                        {isOutOfStock && (
                          <div className="stock-status-overlay">
                            Tükendi
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {selectedColor && (
                  <div className="selected-color-name">
                    Seçilen Renk: <span>{selectedColor.name}</span>
                  </div>
                )}
              </div>
            ))}
            
            <button 
              className={`add-to-cart-button ${(stockStatus.hasStockIssue || loadingColors || !selectedModel || (!selectedColor && availableColors.length > 0) || (selectedColor && stockInfo.stockAmount <= 0)) ? 'disabled' : ''}`}
              onClick={handleAddToCart}
              disabled={
                !selectedModel || 
                loadingColors || 
                stockStatus.hasStockIssue ||
                availableColors.length === 0 ||
                (!selectedColor && availableColors.length > 0) || 
                (selectedColor && stockInfo.stockAmount <= 0)
              }
            >
              <FaShoppingCart className="cart-icon" />
              {!selectedModel ? (
                "Model Seçiniz"
              ) : loadingColors || stockStatus.isChecking ? (
                "Yükleniyor..."
              ) : stockStatus.hasStockIssue ? (
                "Stok Sorunu"
              ) : availableColors.length === 0 ? (
                "Renk Seçeneği Bulunmuyor"
              ) : !selectedColor ? (
                "Renk Seçiniz"
              ) : selectedColor && stockInfo.stockAmount <= 0 ? (
                "Tükendi"
              ) : (
                "Sepete Ekle"
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Stock Warning Modal */}
      <Modal 
        show={showStockWarning} 
        onHide={handleCloseStockWarning} 
        centered
        className="stock-warning-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Stok Uyarısı</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Uyarı!</p>
          <p>{stockWarningMessage}</p>
          <p>Sepetinizde bulunan aynı modeldeki ürünlerden dolayı stok limiti dolmuştur. Lütfen önce sepetinizi kontrol ediniz.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseStockWarning}>
            Kapat
          </Button>
          <Button variant="primary" onClick={() => navigate('/cart')}>
            Sepete Git
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Color info modal */}
      <Modal
        show={showColorInfo}
        onHide={handleCloseColorInfo}
        centered
        className="color-info-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Renk Seçenekleri Hakkında</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Değerli Müşterilerimiz,</p>
          <p>Sublimasyon baskı ile üretilen sert plastik telefon kılıflarımız, yalnızca beyaz, siyah ve transparan yan kenar renklerine sahiptir.</p>
          <ul>
            <li><strong>Beyaz Kılıflar:</strong> Arka yüzeyi baskıya uygundur, yan kenarları beyazdır.</li>
            <li><strong>Siyah Kılıflar:</strong> Arka yüzeyi baskıya uygundur, yan kenarları siyahtır.</li>
            <li><strong>Transparan Kılıflar:</strong> Arka yüzeyi baskıya uygundur, yan kenarları şeffaftır ve telefonunuzun rengini gösterir.</li>
          </ul>
          <p>Baskı işlemi yalnızca kılıfın arka yüzeyine uygulanmaktadır. Yan kenarlara herhangi bir baskı yapılmamaktadır. Sipariş verirken bu bilgiyi göz önünde bulundurmanızı rica ederiz.</p>
          <p>Anlayışınız için teşekkür eder, keyifli alışverişler dileriz!</p>
          <p><strong>Mr Brown Case</strong></p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseColorInfo}>
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Shopping Cart Sidebar */}
      <ShoppingCartSidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        cartItems={formatCartItemsForSidebar()}
        onUpdateQuantity={handleUpdateQuantity}
        onRemoveItem={handleRemoveItem}
        onViewCart={handleViewCart}
        onCheckout={handleCheckout}
      />
    </div>
  );
};

export default ProductDetail; 